import { useCallback } from 'react'
import type { RefObject } from 'react'
import { useParams } from 'react-router-dom'

import type { FragmentOf } from '~publish/graphql'

import type { SavedReplyItem_SavedReply } from '../../../components/SavedRepliesPopover/SavedReplyItem'
import type { InsertedRange } from '../../../components/EngagementInsertedTextHighlight'
import { useEngagementReplyForm } from '../../../components/EngagementReplyForm/hooks/useEngagementReplyForm'
import { useCommunityConfig } from '../../../CommunityConfig'
import { useCommentData } from '../../context/CommentContext'
import { useReplyDrafts } from '../../context/ReplyDraftsContext'
import type { ReplyErrorType, ReplyFormStatus } from '../../../types'
import { isPrivateLinkedInAccount } from '../../../util/privateAccount'
import { formatMentionUsername } from '../utils/replyFormUtils'

import { useCommentReplySubmitStrategy } from './useCommentReplySubmitStrategy'
import type { CommentReply } from './useCommentReplySubmitStrategy'

export type { InsertedRange }

type UseCommentReplyFormProps = {
  channel: Parameters<typeof useEngagementReplyForm>[0]['channel']
  textareaRef: RefObject<HTMLTextAreaElement>
  onExpandNextComment?: (
    fromCommentId?: string,
    onlyRepliable?: boolean,
  ) => void
  onReplyStatusChange?: (status: ReplyFormStatus) => void
  expanded?: boolean
  onCollapse?: () => void
  isResolvedLocally?: boolean
}

export type UseCommentReplyFormReturn = {
  // Form state
  text: string
  status: ReplyFormStatus
  error?: string
  errorType?: ReplyErrorType
  submittingText?: string
  sentReplies: Array<CommentReply> // Local state - new replies not in Apollo cache yet

  // Form actions
  setText: (text: string) => void
  focus: () => void
  clear: () => void
  submit: (e?: React.FormEvent) => Promise<void>

  // UI state
  discard: () => void
  insertSuggestion: (text: string) => boolean
  insertSavedReply: (
    savedReply: FragmentOf<typeof SavedReplyItem_SavedReply>,
  ) => void

  // Inserted-text highlight
  insertedRange: InsertedRange | null
  clearInsertedRange: () => void

  // Refs
  savedRepliesRef: React.RefObject<HTMLDivElement>
  emojiPickerRef: React.RefObject<HTMLDivElement>
}

/**
 * Manages comment reply form state and submission lifecycle.
 *
 * A thin wrapper over the shared `useEngagementReplyForm`: it supplies the
 * comment-specific submit strategy (`useReplyToComment` + `Comment.lastReplies`
 * echo + Pusher reconciliation), draft persistence, the initial-text seed, the
 * expand-next predicate, and the comment card's click-outside selectors — then
 * preserves the existing `UseCommentReplyFormReturn` shape for its consumers.
 *
 * State Machine:
 * ```
 * idle ─[submit]─> processing ─[success]─> published
 *  ↑                  │                         │
 *  └─[retry]──────── error                      └─[submit again]─> (loops)
 * ```
 *
 * States:
 * - `idle`: Ready for input
 * - `processing`: API call in flight, form hidden
 * - `published`: Reply succeeded, persists for styling & allows re-reply
 * - `error`: API failed, form visible for retry
 *
 * The `published` state persists (vs resetting to `idle`) because:
 * 1. Server status won't update immediately (fetchPolicy: 'no-cache')
 * 2. Comment stays visible in "unreplied" filter locally
 * 3. User can keep replying without losing resolved styling
 *
 * @example
 * ```tsx
 * const reply = useCommentReplyForm({ channel, textareaRef })
 * {reply.status === 'processing' && <EngagementReplyProcessing text={reply.submittingText} />}
 * {reply.status !== 'processing' && <ReplyForm onSubmit={reply.submit} />}
 * ```
 */
export const useCommentReplyForm = ({
  channel,
  textareaRef,
  onExpandNextComment,
  onReplyStatusChange,
  expanded = false,
  onCollapse,
  isResolvedLocally = false,
}: UseCommentReplyFormProps): UseCommentReplyFormReturn => {
  const comment = useCommentData()
  const { channelId } = useParams<{ channelId: string }>()
  const { config } = useCommunityConfig()
  const { getDraft, saveDraft, clearDraft } = useReplyDrafts()

  // Initialize from draft if available, otherwise from mention-based seed text.
  const getInitialText = useCallback((): string => {
    const draft = getDraft(comment.id)
    if (draft !== undefined) return draft

    // Skip mention prepending for private LinkedIn accounts.
    if (isPrivateLinkedInAccount({ serviceType: channel.service, comment })) {
      return ''
    }

    // Seed the @mention on channels whose replies support mention-by-text.
    if (config?.isMentionTypeTextSupported(channel)) {
      return `${formatMentionUsername(comment.author.username)} `
    }
    return ''
  }, [getDraft, comment, channel, config])

  // Only auto-advance from an actionable comment that wasn't just resolved.
  const shouldExpandNext =
    (comment.status === 'unreplied' || comment.status === 'new') &&
    !isResolvedLocally

  const onExpandNext = useCallback(() => {
    onExpandNextComment?.(comment.id, true)
  }, [onExpandNextComment, comment.id])

  const form = useEngagementReplyForm<CommentReply>({
    channel,
    textareaRef,
    expanded,
    // Comment id alone is enough; the channel id is folded in so a channel
    // switch (which always swaps the comment too) is covered defensively.
    resetKey: `${channelId ?? ''}:${comment.id}`,
    getInitialText,
    persistence: {
      save: (text) => saveDraft(comment.id, text),
      clear: () => clearDraft(comment.id),
    },
    onStatusChange: onReplyStatusChange,
    shouldExpandNext,
    onExpandNext,
    clickOutside: {
      onCollapse,
      currentCardSelector: `[data-comment-id="${comment.id}"]`,
      cardWrapperSelector: '[data-comment-wrapper="true"]',
    },
    useSubmitStrategy: useCommentReplySubmitStrategy,
  })

  return form
}
